import { conditionalRender } from '@repo/toolkit/conditional-render'
import Input from '@repo/ui/input'
import MultiSelectInput from '@repo/ui/multiselect-input'
import RadioInput from '@repo/ui/radio-input'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'

import { getJourneyPlaces } from '../../../../api/journey-place/get-journey-places'
import { getOptions } from '../../../../api/options/get-options'
import { getPeriods } from '../../../../api/periods/get-periods'
import { getTrips } from '../../../../api/trips/get-trips'
import { ProductDatesFilter } from '../../../../lib/types/models/product/product-date'
import { TripProperties } from '../../../../lib/types/models/trip/properties.trip'
import { getTranslatedName } from '../../../../lib/utils/functions/translation.functions'
import { yearsFilterOptions } from '../../../../views/pcr-personal-space/utils'

interface Props {
  filter?: ProductDatesFilter
  onFilterChange: (filter: ProductDatesFilter) => void
}

export function ProductDateFilter({ filter, onFilterChange }: Props) {
  function setFilter<K extends keyof ProductDatesFilter>(key: K, value: ProductDatesFilter[K]) {
    onFilterChange({ ...filter, [key]: value })
  }

  const { data: periods } = useQuery({
    queryKey: ['periods'],
    queryFn: () => getPeriods({ version: 'BASIC' })
  })

  const { data: journeyPlaces } = useQuery({
    queryKey: ['journey-places'],
    queryFn: () => getJourneyPlaces()
  })

  const { data: options } = useQuery({
    queryKey: ['options'],
    queryFn: () => getOptions()
  })

  const { data: trips } = useQuery({
    queryKey: ['trips'],
    queryFn: () => getTrips()
  })

  return (
    <div className={'flex flex-col gap-3'}>
      <div />
      <div className="flex flex-col gap-2.5">
        <Typography variant="tag">Verrouillé</Typography>
        <div className="flex flex-row gap-5">
          <RadioInput
            checked={filter?.locked === undefined}
            label={'Tous'}
            onCheck={() => {
              onFilterChange({ ...filter, locked: undefined })
            }}
          />
          <RadioInput
            checked={filter?.locked === true}
            label={'Oui'}
            onCheck={() => {
              onFilterChange({ ...filter, locked: true })
            }}
          />
          <RadioInput
            checked={filter?.locked === false}
            label={'Non'}
            onCheck={() => {
              onFilterChange({ ...filter, locked: false })
            }}
          />
        </div>
      </div>

      <div className="flex flex-col gap-2.5">
        <Typography variant="tag">Publié</Typography>
        <div className="flex flex-row gap-5">
          <RadioInput
            checked={filter?.published === undefined}
            label={'Tous'}
            onCheck={() => {
              onFilterChange({ ...filter, published: undefined })
            }}
          />
          <RadioInput
            checked={filter?.published === true}
            label={'Oui'}
            onCheck={() => {
              onFilterChange({ ...filter, published: true })
            }}
          />
          <RadioInput
            checked={filter?.published === false}
            label={'Non'}
            onCheck={() => {
              onFilterChange({ ...filter, published: false })
            }}
          />
        </div>
      </div>

      <MultiSelectInput<number>
        label={'Année'}
        variant={'primary'}
        options={yearsFilterOptions}
        selectedOptions={filter?.years?.map((year) => ({
          label: yearsFilterOptions.find((y) => y.value === year)?.label ?? '',
          value: year
        }))}
        onChange={(years) => setFilter('years', years)}
      />

      <MultiSelectInput
        searchable
        label={'Périodes'}
        variant={'primary'}
        options={periods?.data.map((period) => ({ label: period.name, value: period.name })) ?? []}
        selectedOptions={filter?.periods?.map((period) => ({
          label: periods?.data.find((p) => p.name === period)?.name ?? '',
          value: period
        }))}
        onChange={(periods) => setFilter('periods', periods)}
      />

      <Input
        type={'number'}
        placeholder={'Durée en jours'}
        placeholderVariant="dark"
        value={filter?.durationInDays}
        errorMessage={conditionalRender(
          filter?.durationInDays && filter.durationInDays < 0 ? true : false,
          'La durée doit être supérieur à zéro'
        )}
        onChange={(e) =>
          setFilter('durationInDays', +e.target.value === 0 ? undefined : +e.target.value)
        }
      />

      <MultiSelectInput
        searchable
        label={'Lieux'}
        variant={'primary'}
        options={
          journeyPlaces?.data.map((journeyPlace) => ({
            label: journeyPlace.name,
            value: journeyPlace.name
          })) ?? []
        }
        selectedOptions={filter?.journeyPlaces?.map((journeyPlace) => ({
          label: journeyPlace,
          value: journeyPlace
        }))}
        onChange={(journeyPlaces) => setFilter('journeyPlaces', journeyPlaces)}
      />

      <MultiSelectInput
        searchable
        label={'Thème'}
        variant={'primary'}
        options={
          options?.data.map((mainOption) => ({
            label: getTranslatedName(mainOption),
            value: getTranslatedName(mainOption)
          })) ?? []
        }
        selectedOptions={filter?.mainOptions?.map((mainOption) => ({
          label: mainOption,
          value: mainOption
        }))}
        onChange={(mainOptions) => setFilter('mainOptions', mainOptions)}
      />

      <MultiSelectInput
        searchable
        label={'Options'}
        variant={'primary'}
        options={
          options?.data.map((option) => ({
            label: getTranslatedName(option),
            value: getTranslatedName(option)
          })) ?? []
        }
        selectedOptions={filter?.options?.map((option) => ({
          label: option,
          value: option
        }))}
        onChange={(options) => setFilter('options', options)}
      />

      <MultiSelectInput
        searchable
        label={'Voyages'}
        variant={'primary'}
        options={
          trips?.data.map((option) => ({
            label: TripProperties.getTripLabel(option),
            value: option.id
          })) ?? []
        }
        selectedOptions={filter?.tripIds?.map((tripId) => ({
          label: TripProperties.getTripLabel(trips?.data.find((t) => t.id === tripId)),
          value: tripId
        }))}
        onChange={(tripIds) => setFilter('tripIds', tripIds)}
      />
    </div>
  )
}
